"use client"

import {
    ColumnDef,
    ColumnFiltersState,
    SortingState,
    VisibilityState,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table"
import { MoreHorizontal, Pencil, MapPin, Filter, Printer, Eye } from "lucide-react"
import { format } from "date-fns"

import { Button } from "../../../shared/components/ui/button"
import { Input } from "../../../shared/components/ui/input"
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "../../../shared/components/ui/popover"
import { Calendar } from "../../../shared/components/ui/calendar"
import { Checkbox } from "../../../shared/components/ui/checkbox"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "../../../shared/components/ui/dropdown-menu"
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow as DefaultTableRow,
} from "../../../shared/components/ui/table"
import React, { PropsWithChildren, useEffect } from "react"
import { cn } from "../../../shared/lib/utils"
import Link from "next/link"
import { Dialog, DialogTrigger } from "../../../shared/components/ui/dialog"
import { Avatar, AvatarImage } from "../../../shared/components/ui/avatar"
import { AvatarFallback } from "@radix-ui/react-avatar"
import { WorkOrder } from "../types/work-order.type"
import WorkOrderDeleteDialog from "./work-order-delete-dialog"
import { WORK_ORDERS_ROUTES, DASHBOARD_ROUTE, HOMEOWNERS_ROUTES } from "../../../shared/constants/pages-routes"
import { updateWorkOrderStatus } from "@/features/work-orders/api/work-orders.api"
import { UserRole } from "../../../shared/constants/user-roles"
import { buildHomeownersDetailUrlWithReturn } from "../../../shared/lib/utils/navigation-helpers"
import { buildRegionAwareHref } from "../../../shared/lib/utils"
import { useRegion } from "../../../shared/hooks/use-region"
import {
  maskHomeownerLastName,
  maskHomeownerPhone,
  maskHomeownerAddress,
} from "@/shared/lib/utils/homeowner-privacy.utils"
import { formatWorkScope } from "../utils/format-work-scope"
import { getDisplayAddressValue, isUnknownLocationValue } from "@/features/homeowners/lib/unknown-location"

// Status Toggle Button (Open/Closed, like Confidential On/Off)
function StatusToggleSwitch({
    workOrderId,
    currentStatus,
    onStatusChange,
}: {
    workOrderId: number
    currentStatus: string
    onStatusChange?: (workOrderId: number, newStatus: string) => void
}) {
    const [isLoading, setIsLoading] = React.useState(false)
    const [localStatus, setLocalStatus] = React.useState(currentStatus)
    const [isOptimistic, setIsOptimistic] = React.useState(false)

    // Update local status when currentStatus prop changes, but only if not in optimistic state
    React.useEffect(() => {
        if (!isOptimistic) {
            setLocalStatus(currentStatus)
        }
    }, [currentStatus, isOptimistic])

    const isActive = localStatus === "Active"

    const handleClick = async () => {
        if (!onStatusChange) return

        setIsLoading(true)
        const newStatus = isActive ? "Inactive" : "Active"

        // Optimistically update the local state
        setLocalStatus(newStatus)
        setIsOptimistic(true)

        try {
            await onStatusChange(workOrderId, newStatus)
        } catch (error) {
            console.error("Failed to update work order status:", error)
            setLocalStatus(currentStatus)
            setIsOptimistic(false)
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <Button
            type="button"
            variant={isActive ? "default" : "outline"}
            size="sm"
            className="shrink-0"
            onClick={handleClick}
            disabled={isLoading}
            aria-label={isActive ? "Close work order" : "Reopen work order"}
        >
            {isActive ? "Open" : "Closed"}
        </Button>
    )
}

const InnerTh = (props: PropsWithChildren & { className?: string }) => {
    return (
        <div className={cn("text-left w-max", props.className)}>
            {props.children}
        </div>
    )
}

const SUGGESTIONS_MAX = 80

function ComboboxFilterContent({
    value,
    onApply,
    suggestions,
    placeholder,
}: { value: string; onApply: (v: string | null) => void; suggestions: string[]; placeholder: string }) {
    const [local, setLocal] = React.useState(value)
    const [isListOpen, setIsListOpen] = React.useState(false)
    React.useEffect(() => setLocal(value), [value])
    const filtered = React.useMemo(() => {
        const q = local.trim().toLowerCase()
        const list = q ? suggestions.filter((n) => n.toLowerCase().includes(q)) : suggestions
        return list.slice(0, SUGGESTIONS_MAX)
    }, [suggestions, local])
    const showList = isListOpen && filtered.length > 0
    const hasActive = !!value
    return (
        <div className="w-64 p-2 space-y-2">
            <Input
                value={local}
                onChange={(e) => { setLocal((e.target as HTMLInputElement).value); setIsListOpen(true) }}
                onFocus={() => setIsListOpen(true)}
                onBlur={() => setTimeout(() => setIsListOpen(false), 150)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); const v = (e.target as HTMLInputElement).value.trim(); onApply(v || null); } }}
                placeholder={placeholder}
                className="h-8"
            />
            {showList && (
                <div className="border rounded-md bg-muted/50 max-h-40 overflow-y-auto">
                    {filtered.map((name) => (
                        <button
                            key={name}
                            type="button"
                            className="w-full px-2 py-1.5 text-left text-sm hover:bg-accent focus:bg-accent focus:outline-none"
                            onMouseDown={(e) => { e.preventDefault(); setLocal(name); onApply(name); setIsListOpen(false) }}
                            onClick={() => { setLocal(name); onApply(name); setIsListOpen(false) }}
                        >
                            {name}
                        </button>
                    ))}
                </div>
            )}
            <div className="flex gap-2">
                <Button size="sm" variant="default" onClick={() => onApply(local.trim() || null)}>Apply</Button>
                {hasActive && <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={() => onApply(null)}>Clear</Button>}
            </div>
        </div>
    )
}

function OwnerComboboxFilterContent({
    value,
    onApply,
    ownerOptions,
}: { value: string; onApply: (v: string | null) => void; ownerOptions: { id: number; fullname: string }[] }) {
    const [local, setLocal] = React.useState(value)
    const [isListOpen, setIsListOpen] = React.useState(false)
    const displayValue = ownerOptions.find((o) => o.id.toString() === value)?.fullname ?? value
    React.useEffect(() => { setLocal(displayValue) }, [displayValue])
    const filtered = React.useMemo(() => {
        const q = local.trim().toLowerCase()
        return (q ? ownerOptions.filter((o) => o.fullname.toLowerCase().includes(q)) : ownerOptions).slice(0, SUGGESTIONS_MAX)
    }, [ownerOptions, local])
    const showList = isListOpen && filtered.length > 0
    const hasActive = !!value
    return (
        <div className="w-64 p-2 space-y-2">
            <Input
                value={local}
                onChange={(e) => { setLocal((e.target as HTMLInputElement).value); setIsListOpen(true) }}
                onFocus={() => setIsListOpen(true)}
                onBlur={() => setTimeout(() => setIsListOpen(false), 150)}
                onKeyDown={(e) => {
                    if (e.key === "Enter") {
                        e.preventDefault();
                        const val = (e.target as HTMLInputElement).value.trim();
                        const q = val.toLowerCase();
                        const matches = q ? ownerOptions.filter((o) => o.fullname.toLowerCase().includes(q)) : ownerOptions;
                        const o = ownerOptions.find((x) => x.fullname === val) || matches[0];
                        onApply(o ? o.id.toString() : null);
                    }
                }}
                placeholder="Type to search owner…"
                className="h-8"
            />
            {showList && (
                <div className="border rounded-md bg-muted/50 max-h-40 overflow-y-auto">
                    {filtered.map((o) => (
                        <button
                            key={o.id}
                            type="button"
                            className="w-full px-2 py-1.5 text-left text-sm hover:bg-accent focus:bg-accent focus:outline-none"
                            onMouseDown={(e) => { e.preventDefault(); setLocal(o.fullname); onApply(o.id.toString()); setIsListOpen(false) }}
                            onClick={() => { setLocal(o.fullname); onApply(o.id.toString()); setIsListOpen(false) }}
                        >
                            {o.fullname}
                        </button>
                    ))}
                </div>
            )}
            <div className="flex gap-2">
                <Button size="sm" variant="default" onClick={() => { const o = ownerOptions.find((x) => x.fullname === local.trim()); onApply(o ? o.id.toString() : null) }}>Apply</Button>
                {hasActive && <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={() => onApply(null)}>Clear</Button>}
            </div>
        </div>
    )
}

function AddressFilterContent({ cityFilter, streetFilter, onAddressFilter }: { cityFilter: string; streetFilter: string; onAddressFilter: (opts: { city: string | null; street: string | null }) => void }) {
    const [city, setCity] = React.useState(cityFilter)
    const [street, setStreet] = React.useState(streetFilter)
    React.useEffect(() => { setCity(cityFilter); setStreet(streetFilter) }, [cityFilter, streetFilter])
    const hasActive = !!cityFilter || !!streetFilter
    const apply = () => onAddressFilter({ city: city.trim() || null, street: street.trim() || null })
    return (
        <div className="w-56 p-2 space-y-2">
            <div>
                <label className="text-xs text-muted-foreground">City</label>
                <Input value={city} onChange={(e) => setCity((e.target as HTMLInputElement).value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); apply(); } }} placeholder="Filter by city…" className="h-8 mt-0.5" />
            </div>
            <div>
                <label className="text-xs text-muted-foreground">Street</label>
                <Input value={street} onChange={(e) => setStreet((e.target as HTMLInputElement).value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); apply(); } }} placeholder="Filter by street…" className="h-8 mt-0.5" />
            </div>
            <div className="flex gap-2">
                <Button size="sm" variant="default" onClick={apply}>Apply</Button>
                {hasActive && <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={() => onAddressFilter({ city: null, street: null })}>Clear</Button>}
            </div>
        </div>
    )
}

function WorkScopeFilterContent({ value, onApply, options }: { value: string[]; onApply: (v: string[] | null) => void; options: { value: string; label: string }[] }) {
    const [local, setLocal] = React.useState<string[]>(Array.isArray(value) ? value.slice() : value ? [value] : [])
    React.useEffect(() => { setLocal(Array.isArray(value) ? value.slice() : value ? [value] : []) }, [value])
    const hasActive = local.length > 0
    const toggle = (v: string) => { setLocal((prev) => prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]) }
    return (
        <div className="w-56 p-2 space-y-2" onKeyDown={(e) => { if (e.key !== "Enter") return; if ((e.target as HTMLElement)?.closest?.("[data-filter-action=\"apply\"], [data-filter-action=\"clear\"]")) return; e.preventDefault(); onApply(local.length ? local : null); }}>
            <div className="border rounded-md bg-muted/50 max-h-48 overflow-y-auto">
                {options.map((o) => (
                    <label key={o.value} className="flex items-center gap-2 cursor-pointer rounded px-2 py-1.5 hover:bg-muted/60">
                        <Checkbox checked={local.includes(o.value)} onCheckedChange={() => toggle(o.value)} />
                        <span className="text-sm">{o.label}</span>
                    </label>
                ))}
            </div>
            <div className="flex gap-2">
                <Button size="sm" variant="default" data-filter-action="apply" onClick={() => onApply(local.length ? local : null)}>Apply</Button>
                {hasActive && <Button size="sm" variant="ghost" className="text-muted-foreground" data-filter-action="clear" onClick={() => onApply(null)}>Clear</Button>}
            </div>
        </div>
    )
}

function formatDateForDB(d: Date): string {
    return format(d, "yyyy-MM-dd")
}

function CreatedDateFilterContent({ value, onApply }: { value: string; onApply: (v: string | null) => void }) {
    const [local, setLocal] = React.useState<Date | undefined>(value ? (() => { const [y, m, d] = value.split("-").map(Number); return new Date(y, m - 1, d) })() : undefined)
    React.useEffect(() => { setLocal(value ? (() => { const [y, m, d] = value.split("-").map(Number); return new Date(y, m - 1, d) })() : undefined) }, [value])
    const hasActive = !!value
    return (
        <div className="w-auto p-2 space-y-2" onKeyDown={(e) => { if (e.key !== "Enter") return; if ((e.target as HTMLElement)?.closest?.("[data-filter-action=\"apply\"], [data-filter-action=\"clear\"]")) return; e.preventDefault(); onApply(local ? formatDateForDB(local) : null); }}>
            <Calendar
                mode="single"
                selected={local}
                onSelect={(d) => setLocal(d)}
                initialFocus
            />
            <div className="flex gap-2">
                <Button size="sm" variant="default" data-filter-action="apply" onClick={() => onApply(local ? formatDateForDB(local) : null)}>Apply</Button>
                {hasActive && <Button size="sm" variant="ghost" className="text-muted-foreground" data-filter-action="clear" onClick={() => onApply(null)}>Clear</Button>}
            </div>
        </div>
    )
}

// Open/Closed filter: value is "Active" | "Inactive" (backend); display Open / Closed / All
function StatusFilterContent({ value, onApply }: { value: string | null | undefined; onApply: (v: string | null) => void }) {
    const options = [
        { value: "", label: "All" },
        { value: "Active", label: "Open" },
        { value: "Inactive", label: "Closed" },
    ] as const
    const hasActive = value === "Active" || value === "Inactive"
    return (
        <div className="w-40 p-2 space-y-2">
            <div className="border rounded-md bg-muted/50">
                {options.map((o) => {
                    const selected = (!o.value && !value) || o.value === value
                    return (
                        <button
                            key={o.value || "all"}
                            type="button"
                            className={cn(
                                "w-full px-2 py-1.5 text-left text-sm hover:bg-accent focus:bg-accent focus:outline-none rounded-md",
                                selected && "bg-primary text-primary-foreground hover:bg-primary/90"
                            )}
                            onClick={() => onApply(o.value || null)}
                        >
                            {o.label}
                        </button>
                    )
                })}
            </div>
            {hasActive && (
                <Button size="sm" variant="ghost" className="text-muted-foreground w-full" onClick={() => onApply(null)}>
                    Clear
                </Button>
            )}
        </div>
    )
}

// Homeowner Name cell: links to Home Details when homeowner exists, else to work order details.
// List is filtered to "own" WOs for Vol/HO, so only Supporters see masked data here.
function HomeownerNameCell({
  row,
  userRole,
}: {
  row: { original: WorkOrder }
  userRole?: string | null
  sessionSub?: string | number | null
}) {
  const { regionSlug } = useRegion()
  const raw = row.original.home?.homeowner?.user?.fullname || "No homeowner"
  const mask = userRole === UserRole.Supporter
  const homeownerName = mask ? maskHomeownerLastName(raw) : raw
  const href = row.original.home?.homeowner?.id
    ? buildRegionAwareHref(
        regionSlug,
        buildHomeownersDetailUrlWithReturn(row.original.home.homeowner.id),
      )
    : WORK_ORDERS_ROUTES.details(row.original.id)
  return (
    <Link href={href} className="cursor-pointer hover:underline">
      <div className="max-w-[200px] truncate" title={homeownerName}>
        {homeownerName}
      </div>
    </Link>
  )
}

// Work Order Actions Cell Component
function WorkOrderActionsCell({ row, ...props }: any) {
    const { onStatusChange, canManageWorkOrders, userRole, userId } = props
    const { regionSlug } = useRegion()
    
    // Only Admin/BaseLeader can edit, delete, and change status
    const canEdit = canManageWorkOrders
    const canDelete = canManageWorkOrders
    const canChangeStatus = canManageWorkOrders
    
    return (
        <>
            <div className="flex justify-end md:hidden">
                <Dialog>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="ghost"
                                className="h-8 w-8 p-0"
                            >
                                <span className="sr-only">
                                    Open menu
                                </span>
                                <MoreHorizontal className="h-4 w-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            {canEdit && (
                                <DropdownMenuItem asChild>
                                    <Link href={WORK_ORDERS_ROUTES.edit(row.original.id)}>Edit</Link>
                                </DropdownMenuItem>
                            )}
                            {row.original.home?.id && (
                                <DropdownMenuItem asChild>
                                    <Link href={`${DASHBOARD_ROUTE.map}&homeId=${row.original.home.id}`}>Map</Link>
                                </DropdownMenuItem>
                            )}
                            {canDelete && (
                                <DropdownMenuItem asChild>
                                    <DialogTrigger className="w-full">
                                        Delete
                                    </DialogTrigger>
                                </DropdownMenuItem>
                            )}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </Dialog>
            </div>
            <div className="hidden md:flex items-center justify-end pr-4 w-full">
                <div className="flex gap-1.5 lg:gap-2 items-center">
                    <Button
                        variant={"link"}
                        size={"icon"}
                        className="w-7 h-7 lg:w-8 lg:h-8 bg-foreground rounded-md hover:bg-foreground/90"
                        asChild
                    >
                        <Link
                            href={WORK_ORDERS_ROUTES.details(row.original.id)}
                        >
                            <Eye className="w-4 text-white" />
                        </Link>
                    </Button>
                    <Button
                        variant={"link"}
                        size={"icon"}
                        className="w-7 h-7 lg:w-8 lg:h-8 bg-foreground rounded-md hover:bg-foreground/90"
                        asChild
                    >
                        <Link
                            href={`${WORK_ORDERS_ROUTES.details(row.original.id)}?print=true`}
                        >
                            <Printer className="w-4 text-white" />
                        </Link>
                    </Button>
                    {canEdit && (
                        <Button
                            variant={"link"}
                            size={"icon"}
                            className="w-7 h-7 lg:w-8 lg:h-8 bg-foreground rounded-md hover:bg-foreground/90"
                            asChild
                        >
                            <Link
                                href={WORK_ORDERS_ROUTES.edit(row.original.id)}
                            >
                                <Pencil className="w-4 text-white" />
                            </Link>
                        </Button>
                    )}
                    {row.original.home?.id && (
                        <Button
                            variant={"default"}
                            size={"icon"}
                            asChild
                            className="rounded-md bg-foreground hover:bg-foreground/90 text-white w-7 h-7 lg:w-8 lg:h-8"
                        >
                            <Link href={buildRegionAwareHref(regionSlug, `${HOMEOWNERS_ROUTES.map}&homeId=${row.original.home.id}`)}>
                                <MapPin className="w-4 h-4" />
                            </Link>
                        </Button>
                    )}
                    {canDelete && (
                        <WorkOrderDeleteDialog
                            workOrderId={row.original.id}
                            orderNumber={row.original.orderNumber}
                        />
                    )}
                </div>
            </div>
        </>
    )
}

type FilterHeaderProps = {
    label: string
    hasActive: boolean
    children: React.ReactNode
}
function FilterHeader({ label, hasActive, children }: FilterHeaderProps) {
    return (
        <InnerTh>
            <div className="flex items-center gap-1.5">
                <span>{label}</span>
                <Popover>
                    <PopoverTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-7 w-7 rounded" aria-label={`Filter by ${label}`}>
                            <Filter className={cn("h-3.5 w-3.5", hasActive && "text-primary")} />
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-auto p-0">
                        {children}
                    </PopoverContent>
                </Popover>
            </div>
        </InnerTh>
    )
}

type GetColumnsProps = {
    onStatusChange?: (workOrderId: number, newStatus: string) => Promise<void>
    canManageWorkOrders?: boolean
    userRole?: string | null
    userId?: number | null
    sessionSub?: string | number | null
    homeownerNameFilter?: string
    onHomeownerNameFilter?: (v: string | null) => void
    homeownerNames?: string[]
    cityFilter?: string
    streetFilter?: string
    onAddressFilter?: (opts: { city: string | null; street: string | null }) => void
    workScopeFilter?: string[]
    onWorkScopeFilter?: (v: string[] | null) => void
    workScopeOptions?: { value: string; label: string }[]
    ownerIdFilter?: string
    onOwnerFilter?: (v: string | null) => void
    ownerOptions?: { id: number; fullname: string }[]
    teamLeadFilter?: string
    onTeamLeadFilter?: (v: string | null) => void
    teamLeadNames?: string[]
    teamNameFilter?: string
    onTeamNameFilter?: (v: string | null) => void
    teamNames?: string[]
    createdDateFilter?: string
    onCreatedDateFilter?: (v: string | null) => void
    statusFilter?: string | null
    onStatusFilter?: (v: string | null) => void
}

function getColumns(p: GetColumnsProps): ColumnDef<WorkOrder>[] {
    return [
        {
            id: "select",
            header: ({ table }) => (
                <Checkbox
                    checked={table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && "indeterminate")}
                    onCheckedChange={(v) => table.toggleAllPageRowsSelected(!!v)}
                    aria-label="Select all"
                    className="rounded-md border-muted-foreground/70 data-[state=checked]:border-primary"
                />
            ),
            cell: ({ row }) => (
                <Checkbox
                    checked={row.getIsSelected()}
                    onCheckedChange={(v) => row.toggleSelected(!!v)}
                    aria-label="Select row"
                    className="rounded-md border-muted-foreground/70 data-[state=checked]:border-primary"
                />
            ),
            enableSorting: false,
            enableHiding: false,
        },
        {
            accessorKey: "homeownerName",
            header: () =>
                p.onHomeownerNameFilter ? (
                    <FilterHeader hasActive={!!p.homeownerNameFilter} label="Homeowner Name">
                        <ComboboxFilterContent
                            value={p.homeownerNameFilter ?? ""}
                            onApply={p.onHomeownerNameFilter}
                            suggestions={p.homeownerNames ?? []}
                            placeholder="Type to search names…"
                        />
                    </FilterHeader>
                ) : (
                    <InnerTh>Homeowner Name</InnerTh>
                ),
            cell: ({ row }) => (
              <HomeownerNameCell row={row} userRole={p.userRole} />
            ),
        },
        {
            accessorKey: "homeAddress",
            header: () =>
                p.onAddressFilter ? (
                    <FilterHeader hasActive={!!p.cityFilter || !!p.streetFilter} label="Homeowner Address">
                        <AddressFilterContent cityFilter={p.cityFilter ?? ""} streetFilter={p.streetFilter ?? ""} onAddressFilter={p.onAddressFilter} />
                    </FilterHeader>
                ) : (
                    <InnerTh>Homeowner Address</InnerTh>
                ),
            cell: ({ row }) => {
                const rawStored = row.original.home?.address
                const raw = getDisplayAddressValue(rawStored) || "No address"
                const mask = p.userRole === UserRole.Supporter
                const homeAddress = mask && raw !== "No address" && !isUnknownLocationValue(rawStored)
                  ? maskHomeownerAddress(rawStored)
                  : raw
                return (
                    <Link href={WORK_ORDERS_ROUTES.details(row.original.id)} className="cursor-pointer hover:underline">
                        <div className="max-w-[200px] truncate" title={homeAddress}>{homeAddress}</div>
                    </Link>
                )
            },
        },
        {
            accessorKey: "phoneNumber",
            header: () => <InnerTh>Phone Number</InnerTh>,
            cell: ({ row }) => {
                const phone = row.original.home?.homeowner?.user?.phone
                const mask = p.userRole === UserRole.Supporter
                if (!phone) return <div className="text-left text-muted-foreground">N/A</div>
                const display = mask ? maskHomeownerPhone(phone) : phone
                if (mask) {
                  return (
                    <span className="text-foreground max-w-[160px] truncate block" title={display}>
                      {display}
                    </span>
                  )
                }
                return (
                    <a href={`tel:${phone.replace(/[^\d+]/g, "")}`} className="text-primary hover:underline max-w-[160px] truncate block" title={phone}>
                        {phone}
                    </a>
                )
            },
        },
        {
            id: "workOrderType",
            accessorKey: "workOrderType",
            header: () =>
                p.onWorkScopeFilter ? (
                    <FilterHeader hasActive={(Array.isArray(p.workScopeFilter) ? p.workScopeFilter.length : 0) > 0} label="Work Order">
                        <WorkScopeFilterContent value={p.workScopeFilter ?? []} onApply={p.onWorkScopeFilter} options={p.workScopeOptions ?? []} />
                    </FilterHeader>
                ) : (
                    <InnerTh>Work Order</InnerTh>
                ),
            cell: ({ row }) => {
                const workOrder = row.original
                const workScopeDisplay = workOrder.workScope?.workScopeDisplay || formatWorkScope(workOrder.workOrderType)
                return (
                    <Link href={WORK_ORDERS_ROUTES.details(workOrder.id)} className="text-left hover:underline">
                        {workScopeDisplay}
                    </Link>
                )
            },
        },
        {
            accessorKey: "owner",
            header: () =>
                p.onOwnerFilter ? (
                    <FilterHeader hasActive={!!p.ownerIdFilter} label="Owner">
                        <OwnerComboboxFilterContent value={p.ownerIdFilter ?? ""} onApply={p.onOwnerFilter} ownerOptions={p.ownerOptions ?? []} />
                    </FilterHeader>
                ) : (
                    <InnerTh>Owner</InnerTh>
                ),
            cell: ({ row }) => {
                const owner = row.original.owner
                return (
                    <div className="text-left flex items-center gap-2">
                        <Avatar className="flex items-center justify-center bg-primary text-white w-8 h-8">
                            <AvatarImage src={owner?.profilePicture ?? undefined} alt="" className="object-cover object-top" />
                            <AvatarFallback>{owner ? owner.fullname.split(" ").map((n) => n[0]).join("") : "NA"}</AvatarFallback>
                        </Avatar>
                        <div><h4>{owner ? owner.fullname : "Not assigned"}</h4></div>
                    </div>
                )
            },
        },
        {
            accessorKey: "teamLead",
            header: () =>
                p.onTeamLeadFilter ? (
                    <FilterHeader hasActive={!!p.teamLeadFilter} label="Team Lead">
                        <ComboboxFilterContent value={p.teamLeadFilter ?? ""} onApply={p.onTeamLeadFilter} suggestions={p.teamLeadNames ?? []} placeholder="Type to search…" />
                    </FilterHeader>
                ) : (
                    <InnerTh>Team Lead</InnerTh>
                ),
            cell: ({ row }) => {
                const teamLead = row.original.teamLead
                return (
                    <div className="text-left flex items-center gap-2">
                        <Avatar className="flex items-center justify-center bg-primary text-white w-8 h-8">
                            <AvatarImage src={teamLead?.profilePicture ?? undefined} alt="" className="object-cover object-top" />
                            <AvatarFallback>{teamLead ? teamLead.fullname.split(" ").map((n) => n[0]).join("") : "NA"}</AvatarFallback>
                        </Avatar>
                        <div><h4>{teamLead ? teamLead.fullname : "Not assigned"}</h4></div>
                    </div>
                )
            },
        },
        {
            accessorKey: "teamName",
            header: () =>
                p.onTeamNameFilter ? (
                    <FilterHeader hasActive={!!p.teamNameFilter} label="Team Name">
                        <ComboboxFilterContent value={p.teamNameFilter ?? ""} onApply={p.onTeamNameFilter} suggestions={p.teamNames ?? []} placeholder="Type to search…" />
                    </FilterHeader>
                ) : (
                    <InnerTh>Team Name</InnerTh>
                ),
            cell: ({ row }) => <div className="text-left">{row.original.teamName || "Not assigned"}</div>,
        },
        {
            accessorKey: "createdAt",
            header: () =>
                p.onCreatedDateFilter ? (
                    <FilterHeader hasActive={!!p.createdDateFilter} label="Created Date">
                        <CreatedDateFilterContent value={p.createdDateFilter ?? ""} onApply={p.onCreatedDateFilter} />
                    </FilterHeader>
                ) : (
                    <InnerTh>Created Date</InnerTh>
                ),
            cell: ({ row }) => {
                const createdAt = row.original.createdAt
                return <div className="text-left">{createdAt ? new Date(createdAt).toLocaleDateString() : "No date"}</div>
            },
        },
        {
            accessorKey: "status",
            id: "status",
            header: () =>
                p.onStatusFilter ? (
                    <FilterHeader hasActive={p.statusFilter === "Active" || p.statusFilter === "Inactive"} label="Status">
                        <StatusFilterContent value={p.statusFilter ?? null} onApply={p.onStatusFilter} />
                    </FilterHeader>
                ) : (
                    <InnerTh>Status</InnerTh>
                ),
            cell: ({ row }) => {
                const status = row.original.status
                const isActive = status === "Active"
                const displayText = isActive ? "Open" : "Closed"
                if (p.canManageWorkOrders && p.onStatusChange) {
                    return (
                        <StatusToggleSwitch
                            workOrderId={row.original.id}
                            currentStatus={status}
                            onStatusChange={p.onStatusChange}
                        />
                    )
                }
                return <div className="text-left">{displayText}</div>
            },
        },
        {
            id: "actions",
            enableHiding: false,
            header: () => <InnerTh className="ml-auto" />,
            cell: ({ row }) => <WorkOrderActionsCell row={row} onStatusChange={p.onStatusChange} canManageWorkOrders={p.canManageWorkOrders} userRole={p.userRole} userId={p.userId} />,
        },
    ]
}

export default function WorkOrderTable({
    companyList: data,
    isLoading = false,
    onStatusChange = async () => {},
    canManageWorkOrders = false,
    userRole = null,
    userId = null,
    sessionSub = null,
    homeownerNameFilter,
    onHomeownerNameFilter,
    homeownerNames = [],
    cityFilter,
    streetFilter,
    onAddressFilter,
    workScopeFilter,
    onWorkScopeFilter,
    workScopeOptions = [],
    ownerIdFilter,
    onOwnerFilter,
    ownerOptions = [],
    teamLeadFilter,
    onTeamLeadFilter,
    teamLeadNames = [],
    teamNameFilter,
    onTeamNameFilter,
    teamNames = [],
    createdDateFilter,
    onCreatedDateFilter,
    statusFilter,
    onStatusFilter,
    hasActiveFilters = false,
}: {
    companyList: any[]
    isLoading?: boolean
    onStatusChange?: (workOrderId: number, newStatus: string) => Promise<void>
    canManageWorkOrders?: boolean
    userRole?: string | null
    userId?: number | null
    sessionSub?: string | number | null
    homeownerNameFilter?: string
    onHomeownerNameFilter?: (v: string | null) => void
    homeownerNames?: string[]
    cityFilter?: string
    streetFilter?: string
    onAddressFilter?: (opts: { city: string | null; street: string | null }) => void
    workScopeFilter?: string[]
    onWorkScopeFilter?: (v: string[] | null) => void
    workScopeOptions?: { value: string; label: string }[]
    ownerIdFilter?: string
    onOwnerFilter?: (v: string | null) => void
    ownerOptions?: { id: number; fullname: string }[]
    teamLeadFilter?: string
    onTeamLeadFilter?: (v: string | null) => void
    teamLeadNames?: string[]
    teamNameFilter?: string
    onTeamNameFilter?: (v: string | null) => void
    teamNames?: string[]
    createdDateFilter?: string
    onCreatedDateFilter?: (v: string | null) => void
    statusFilter?: string | null
    onStatusFilter?: (v: string | null) => void
    hasActiveFilters?: boolean
}) {
    const workOrders = Array.isArray(data) ? data : []
    const isVolunteerOrTeamLeader =
        userRole === UserRole.VOLUNTEER || userRole === UserRole.TEAM_LEADER
    const emptyStateMessage =
        hasActiveFilters && isVolunteerOrTeamLeader
            ? "You have not been assigned to any Work Orders filtered."
            : userRole === UserRole.VOLUNTEER
                ? "You are not assigned to any Work Orders"
                : "No data found"

    const allColumns = getColumns({
        onStatusChange,
        canManageWorkOrders,
        userRole,
        userId,
        sessionSub,
        homeownerNameFilter,
        onHomeownerNameFilter,
        homeownerNames,
        cityFilter,
        streetFilter,
        onAddressFilter,
        workScopeFilter,
        onWorkScopeFilter,
        workScopeOptions,
        ownerIdFilter,
        onOwnerFilter,
        ownerOptions,
        teamLeadFilter,
        onTeamLeadFilter,
        teamLeadNames,
        teamNameFilter,
        onTeamNameFilter,
        teamNames,
        createdDateFilter,
        onCreatedDateFilter,
        statusFilter,
        onStatusFilter,
    })
    const resolvedColumns = canManageWorkOrders ? allColumns : allColumns.filter((col) => col.id !== "actions")

    const [sorting, setSorting] = React.useState<SortingState>([])
    const [columnFilters, setColumnFilters] =
        React.useState<ColumnFiltersState>([])
    const [columnVisibility, setColumnVisibility] =
        React.useState<VisibilityState>({})
    const [rowSelection, setRowSelection] = React.useState({})
    // const { setRows } = useSelectedTableRowsContext()

    const table = useReactTable({
        data: workOrders,
        columns: resolvedColumns,
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        onColumnVisibilityChange: setColumnVisibility,
        onRowSelectionChange: setRowSelection,
        state: {
            sorting,
            columnFilters,
            columnVisibility,
            rowSelection,
        },
        getRowId: (originalRow) => originalRow.id.toString(),
    })

    // This useEffect show delete button when rows are selected button
    // and hide when no rows are selected
    // useEffect(() => {
    //     setRows(rowSelection)
    // }, [rowSelection])

    return (
        <div className="w-full">
            <div className="relative max-h-[min(70vh,56rem)] w-full overflow-auto rounded-md">
                <Table noScrollWrapper>
                    <TableHeader className="[&_tr]:border-b-0 [&_th]:sticky [&_th]:top-0 [&_th]:z-30 [&_th]:bg-muted [&_th]:shadow-[0_1px_0_0_hsl(var(--border))]">
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    return (
                                        <TableHead
                                            key={header.id}
                                            className="w-max"
                                        >
                                            {header.isPlaceholder
                                                ? null
                                                : flexRender(
                                                      header.column.columnDef
                                                          .header,
                                                      header.getContext()
                                                  )}
                                        </TableHead>
                                    )
                                })}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            // Loading state
                            Array.from({ length: 5 }).map((_, index) => (
                                <TableRow key={`loading-${index}`}>
                                    {Array.from({ length: resolvedColumns.length }).map(
                                        (_, cellIndex) => (
                                            <TableCell
                                                key={`loading-cell-${index}-${cellIndex}`}
                                            >
                                                <div className="h-4 bg-muted animate-pulse rounded" />
                                            </TableCell>
                                        )
                                    )}
                                </TableRow>
                            ))
                        ) : table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map((row) => (
                                <TableRow
                                    key={row.id}
                                    data-state={
                                        row.getIsSelected() && "selected"
                                    }
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <TableCell key={cell.id}>
                                            {flexRender(
                                                cell.column.columnDef.cell,
                                                cell.getContext()
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell
                                    colSpan={resolvedColumns.length}
                                    className="h-24 text-center"
                                >
                                    <span className='text-muted-foreground mt-5 h-10'>
                                        {emptyStateMessage}
                                    </span>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
            {/* <MyPagination totalPages={totalPages} page={page} /> */}
        </div>
    )
}

const TableRow = React.forwardRef<
    HTMLTableRowElement,
    React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
    <DefaultTableRow
        ref={ref}
        className={cn("border-b-0", className)}
        {...props}
    />
))
TableRow.displayName = "TableRow"
